// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Injectable, NgZone } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { BehaviorSubject, Subscription } from 'rxjs';
import { HceLoadingOverlayComponent } from '../../../shared/hce/hce-loading-overlay/hce-loading-overlay.component';
import { HceLoadingTask } from '../../../shared/hce/hce-loading-overlay/types';

@Injectable({ providedIn: 'root' })
export class HceLoadingOverlayService {
  // ===== Overlay refs =====
  private overlayRef?: OverlayRef;
  private cmpRef?: import('@angular/core').ComponentRef<HceLoadingOverlayComponent>;

  // ===== State =====
  private title$ = new BehaviorSubject<string>('');
  private tasks$ = new BehaviorSubject<HceLoadingTask[]>([]);
  private subs: Subscription[] = [];
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(private overlay: Overlay, private zone: NgZone) {}

  // ===== Public API =====

  /** Open overlay and bind reactive state. No-op when already opened. */
  open(initialTitle = ''): void {
    if (this.overlayRef) {
      return;
    }

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'hce-overlay-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    this.cmpRef = this.overlayRef.attach(new ComponentPortal(HceLoadingOverlayComponent));

    // Initialize title
    this.title$.next(initialTitle);

    // Bind streams to component instance
    this.subs.push(this.title$.subscribe(t => {
      if (this.cmpRef) {
        this.cmpRef.instance.title = t;
        this.cmpRef.changeDetectorRef.detectChanges();
      }
    }));

    this.subs.push(this.tasks$.subscribe(list => {
      if (this.cmpRef) {
        this.cmpRef.instance.tasks = this.sortTasks(list);
        this.cmpRef.changeDetectorRef.detectChanges();
      }
    }));
  }

  /** Close overlay and reset internal state. */
  close() {
    // Unsubscribe all bindings to component
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];

    // Dispose overlay
    this.overlayRef?.detach();
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.cmpRef = undefined;

    // Reset state
    this.title$.next('');
    this.tasks$.next([]);

    // Clear pending timers
    for (const h of this.timers.values()) {
      try { clearTimeout(h); } catch {}
    }
    this.timers.clear();
  }

  /** Update overlay title (phase headline). No-op when not opened. */
  setTitle(title: string) {
    if (!this.overlayRef) {
      return;
    }
    this.title$.next(title);
  }

  /** Remove all tasks (does not close the overlay). */
  resetTasks() {
    this.tasks$.next([]);
  }

  /** Start (or upsert) a task in 'running' state. */
  startTask(task: Omit<HceLoadingTask, 'status' | 'startedAt'> & { minDisplayMs?: number}) {
    const now = performance.now();
    const next: HceLoadingTask = {
      ...task,
      status: 'running',
      startedAt: now,
      minDisplayMs: task.minDisplayMs ?? 700,
    };
    const arr = this.upsert(this.tasks$.value, next);
    this.tasks$.next(arr);
  }

  /** Immediately mark a task as done, ignoring minDisplayMs. */
  forceCompleteTask(id: string) {
    this.zone.run(() => {
      const curr = this.tasks$.value;
      const idx = curr.findIndex(t => t.id === id);
      if (idx < 0) {
        return;
      }

      // Clear pending delayed-complete timer if any
      const handle = this.timers.get(id);
      if (handle) {
        try { clearTimeout(handle); } catch {}
        this.timers.delete(id);
      }
      
      const copy = curr.slice();
      copy[idx] = {
        ...copy[idx],
        status: 'done',
        completedAt: performance.now()
      };
      this.tasks$.next(copy);

      // Force paint when component attached
      this.cmpRef?.changeDetectorRef.detectChanges();
    });
  }

  /** Force complete for multiple task ids. */
  forceCompleteTasks(ids: string[]) {
    ids.forEach(id => this.forceCompleteTask(id));
  }

  /** Respect minDisplayMs to avoid flicker, and update using latest snapshot. */
  completeTask(id: string) {
    const initial = this.tasks$.value.find(t => t.id === id)
    if (!initial) {
      return;
    }
    const now = performance.now();
    const elapsed = (initial.startedAt ? now - initial.startedAt : 0);
    const wait = Math.max(0, (initial.minDisplayMs ?? 700) - elapsed);

    const apply = () => {
      this.zone.run(() => {
        // always read latest snapshot
        const updated = this.tasks$.value.map<HceLoadingTask>(t =>
          t.id === id ? { ...t, status: 'done', completedAt: performance.now() } : t
        );
        this.tasks$.next(updated);
        this.cmpRef?.changeDetectorRef.detectChanges();
      });
    };

    if (wait <= 0) {
      apply();
    } else {
      const handle = setTimeout(apply, wait);
      this.timers.set(id, handle);
    }
  }

  /** Mark task as error. (Style representation handled by component) */
  errorTask(id: string, labelOverride?: string) {
    const tasks = this.tasks$.value.slice();
    const idx = tasks.findIndex(t => t.id === id);
    if (idx < 0) {
      return;
    }
    const t = tasks[idx];
    tasks[idx] = {
      ...t,
      status: 'error',
      label: labelOverride ?? t.label,
      completedAt: performance.now(),
    };
    this.tasks$.next(tasks);
  }

  /** Force CD + wait for N animation frames to ensure paint before heavy sync work. */
  async flush(frames: number = 1): Promise<void> {
    this.cmpRef?.changeDetectorRef.detectChanges();
    await new Promise<void>(r => requestAnimationFrame(() => r()));
    for (let i = 1; i < frames; i++) {
      await new Promise<void>(r => requestAnimationFrame(() => r()));
    }
  }

  // ===== helpers =====

  private upsert(arr: HceLoadingTask[], t: HceLoadingTask) {
    const idx = arr.findIndex(x => x.id === t.id);
    if (idx >= 0) {
      const copy = arr.slice();
      copy[idx] = { ...arr[idx], ...t };
      return copy;
    }
    return [...arr, t];
  }

  /** Sort running first by start time; then done by completed time. */
  private sortTasks(list: HceLoadingTask[]) {
    const running = list
      .filter(t => t.status !== 'done')
      .sort((a, b) => (a.startedAt ?? 0) - (b.startedAt ?? 0));
    const done = list
      .filter(t => t.status === 'done')
      .sort((a, b) => (a.completedAt ?? 0) - (b.completedAt ?? 0));
    return [...running, ...done];
  }
}
